import { expressURL, getAuthHeaders } from '../utils/networkUtils';

type FetchOptions = Omit<RequestInit, 'body'> & { body?: unknown };

export class AccessApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'AccessApiError';
    this.status = status;
  }
}

function apiUrl(path: string): string {
  return `${expressURL().replace(/\/$/, '')}${path}`;
}

function parseJson(text: string): unknown {
  if (!text.trim()) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

function errorMessage(body: unknown, response: Response): string {
  if (body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string') {
    return (body as { error: string }).error;
  }
  return `Court-access request failed (HTTP ${response.status})`;
}

export async function accessFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const authConfig = getAuthHeaders() as { headers?: Record<string, string> };
  const headers = new Headers(authConfig.headers);
  if (options.body !== undefined) headers.set('Content-Type', 'application/json');
  headers.set('Accept', 'application/json');
  const response = await fetch(apiUrl(path), {
    ...options,
    credentials: 'include',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body)
  });
  const text = await response.text();
  const parsed = parseJson(text);
  if (!response.ok) {
    throw new AccessApiError(errorMessage(parsed, response), response.status);
  }
  return (parsed === undefined ? undefined : parsed) as T;
}

export type AccessUser = { id: string; shortName?: string; fullName?: string };

export type AccessCustodianSummary = {
  id: string;
  role: 'directorate' | 'depot' | 'provider';
  index: number;
  holderUserId: string;
  custodyPublicKeyJwk?: JsonWebKey;
  shareDeposited?: boolean;
};

export type AccessOverview = {
  mode: string;
  labKeyRegistered: boolean;
  labPublicKeyJwk: JsonWebKey | null;
  custodySets: Array<{
    id: string;
    targetUserId: string;
    version: number;
    state: string;
    periodId: string | null;
    custodians: AccessCustodianSummary[];
  }>;
  orders: Array<{
    id: string;
    custodySetId: string;
    targetUserId: string;
    periodId: string;
    periodStart: string;
    periodEnd: string;
    expiresAt: string;
    corpusScope: string[];
    orderHash: string;
    caseReference?: string;
    state: string;
  }>;
  sessions: Array<{
    id: string;
    orderId: string;
    custodySetId: string;
    targetUserId: string;
    state: string;
    expiresAt: string;
    releaseCount: number;
    threshold: number;
    caseReference?: string;
    signedOrderHash?: string | null;
    sessionPublicKeyJwk?: JsonWebKey;
    registryPublicKeyJwk?: JsonWebKey;
    registrySignature?: string;
    limitation?: string;
  }>;
  assignments: Array<{
    custodySetId: string;
    custodianId: string;
    role: string;
    index: number;
    shareDeposited: boolean;
  }>;
  users: AccessUser[];
  limitation: string;
};

export const accessApi = {
  getOverview() {
    return accessFetch<AccessOverview>('/api/access/overview');
  },
  registerLabKey(publicKeyJwk: JsonWebKey) {
    return accessFetch<{ publicKeyJwk: JsonWebKey; publicKeyFingerprint: string }>(
      '/api/access/lab-key',
      { method: 'PUT', body: { publicKeyJwk } }
    );
  },
  getLabKey(userId: string) {
    return accessFetch<{ userId: string; publicKeyJwk: JsonWebKey; publicKeyFingerprint: string }>(
      `/api/access/lab-keys/${encodeURIComponent(userId)}`
    );
  },
  createCustodySet(body: {
    targetUserId: string;
    custodians: Array<{
      role: string;
      holderUserId: string;
      custodyPublicKeyJwk: JsonWebKey;
    }>;
  }) {
    return accessFetch<{ custodySet: AccessOverview['custodySets'][number] }>(
      '/api/access/custody-sets',
      { method: 'POST', body }
    );
  },
  activateCustodySet(id: string, body: Record<string, unknown>) {
    return accessFetch<{ custodySet: AccessOverview['custodySets'][number] }>(
      `/api/access/custody-sets/${encodeURIComponent(id)}/activate`,
      { method: 'POST', body }
    );
  },
  getMyShare(custodySetId: string) {
    return accessFetch<{
      custodySetId: string;
      custodianId: string;
      role: 'directorate' | 'depot' | 'provider';
      index: number;
      periodId: string;
      custodyPublicKeyJwk: JsonWebKey;
      custodyPublicKeyFingerprint: string;
      activationManifest: Record<string, unknown>;
      envelope: unknown;
    }>(`/api/access/custody-sets/${encodeURIComponent(custodySetId)}/my-share`);
  },
  createOrder(body: Record<string, unknown>) {
    return accessFetch<{ order: AccessOverview['orders'][number] }>(
      '/api/access/orders',
      { method: 'POST', body }
    );
  },
  createSession(body: { orderId: string; sessionPublicKeyJwk: JsonWebKey }) {
    return accessFetch<{ session: AccessOverview['sessions'][number] }>(
      '/api/access/sessions',
      { method: 'POST', body }
    );
  },
  getSession(id: string) {
    return accessFetch<{
      session: AccessOverview['sessions'][number];
      order: AccessOverview['orders'][number];
      signedOrder: {
        payload: {
          protocolDomain: 'court-access';
          protocolVersion: 1;
          targetUserId: string;
          periodStart: string;
          periodEnd: string;
          expiresAt: string;
          corpusScope: string[];
          sessionPublicKeyFingerprint: string;
        };
        signedOrderHash: string;
        registryPublicKeyJwk: JsonWebKey;
        registrySignature: string;
      };
      releases: Array<{ role: string; holderUserId: string }>;
      attestations: Array<{ orderHash: string; sessionFingerprint: string; matchedScope: boolean }>;
      target: AccessUser;
    }>(`/api/access/sessions/${encodeURIComponent(id)}`);
  },
  getView(id: string) {
    return accessFetch<{
      session: AccessOverview['sessions'][number];
      order: AccessOverview['orders'][number];
      signedOrder: {
        payload: {
          protocolDomain: 'court-access';
          protocolVersion: 1;
          targetUserId: string;
          periodStart: string;
          periodEnd: string;
          expiresAt: string;
          corpusScope: string[];
          sessionPublicKeyFingerprint: string;
        };
        signedOrderHash: string;
        registryPublicKeyJwk: JsonWebKey;
        registrySignature: string;
      };
      sealedShares: Array<{
        role: string;
        index: number;
        custodianId: string;
        envelope: unknown;
        context: unknown;
      }>;
      releases: Array<{ role: string; holderUserId: string }>;
      attestations: Array<{ orderHash: string; sessionFingerprint: string; matchedScope: boolean }>;
      target: AccessUser;
    }>(`/api/access/sessions/${encodeURIComponent(id)}/view`);
  },
  getCorpus(id: string) {
    return accessFetch<{
      threads: Array<{
        id: number;
        name: string;
        roomType: string;
        encryptionEnabled?: boolean;
        unwrapAvailable?: boolean;
        correspondents?: Array<{ label: string; isTarget: boolean }>;
      }>;
      omissions?: Array<{ threadId: number | null; roomType: string; reason: string }>;
      limitations?: string[];
      correspondents?: Array<{ label: string; isTarget: boolean }>;
    }>(
      `/api/access/sessions/${encodeURIComponent(id)}/corpus`
    );
  },
  release(sessionId: string, sealedShare: unknown) {
    return accessFetch<{ session: AccessOverview['sessions'][number] }>(
      `/api/access/sessions/${encodeURIComponent(sessionId)}/releases`,
      { method: 'POST', body: { sealedShare } }
    );
  },
  attest(sessionId: string, matchedScope: boolean) {
    return accessFetch(
      `/api/access/sessions/${encodeURIComponent(sessionId)}/attest`,
      { method: 'POST', body: { matchedScope } }
    );
  },
  close(sessionId: string) {
    return accessFetch(
      `/api/access/sessions/${encodeURIComponent(sessionId)}/close`,
      { method: 'POST', body: {} }
    );
  },
  loadThreads(sessionId: string, threadIds: number[]) {
    return accessFetch<{
      threadIds: number[];
      roomTypes: string[];
      threads?: Array<{
        id: number;
        name: string;
        roomType: string;
        encryptionEnabled?: boolean;
        unwrapAvailable?: boolean;
        correspondents?: Array<{ label: string; isTarget: boolean }>;
      }>;
      periodIds: string[];
      periodEnvelopes: unknown[];
      accessPeriodWraps: Array<{ threadId: number; wrap: unknown }>;
      ciphertext: Array<{
        threadId: number;
        messageId: number;
        time: string;
        type: string;
        ciphertext: string;
        senderLabel?: string;
      }>;
      omissions?: Array<{ threadId: number | null; roomType: string; reason: string }>;
      limitations?: string[];
      correspondents?: Array<{ label: string; isTarget: boolean }>;
    }>(`/api/access/sessions/${encodeURIComponent(sessionId)}/threads`, {
      method: 'POST',
      body: { threadIds }
    });
  },
  pendingDeposit() {
    return accessFetch<{
      pending: {
        custodySetId: string;
        version: number;
        suggestedPeriodId: string | null;
        custodians: AccessCustodianSummary[];
      } | null;
    }>('/api/crypto/access-custody-pending');
  },
  depositShares(body: Record<string, unknown>) {
    return accessFetch('/api/crypto/access-custody-deposit', { method: 'POST', body });
  },
  lookupUser(email: string) {
    return accessFetch<AccessUser & { id: string }>(
      `/api/lookup-user?email=${encodeURIComponent(email)}`
    );
  }
};
